import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageCircle, Search, Send, X } from 'lucide-react';
import { toast } from 'react-toastify';
import Axios from '../../networking/intraceptor';
import {
	pharmacy_patient_chats,
	pharmacy_patient_chats_read,
	pharmacy_patient_chat_patients,
	pharmacy_patient_chats_unread_summary,
} from '../../networking/urlEndPoints';
import { subscribeRealtimeEvent } from '../../services/realtime/realtimeSocket';
import { pharmacy_app_patient_chat } from '../../routes/routeName';

const OPEN_EVENT = 'oatrx:patient-chat:open';
const CHAT_EVENT = 'pharmacy-patient-chat-message';
const READ_EVENT = 'pharmacy-patient-chat-read';
const UNREAD_CHANGED_EVENT = 'oatrx:patient-chat:unread-changed';
const UNREAD_STORAGE_KEY = 'oatrx:patient-chat:unread-summary';
const NOTIFICATION_TOAST_ID_PREFIX = 'patient-chat-message';
const PATIENT_CHAT_BRAND_BLUE = '#1A56DB';
const PATIENT_CHAT_NOTIFICATION_TOAST_STYLE = {
	background: '#ffffff',
	border: '1px solid #e5e7eb',
	borderRadius: '8px',
	boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.1)',
	padding: '16px',
	minHeight: 'auto',
	margin: 0,
};

const PatientChatMessageNotificationContent = ({ patientName, preview }) => (
	<div className="flex cursor-pointer items-start gap-3">
		<div
			className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white"
			style={{ backgroundColor: PATIENT_CHAT_BRAND_BLUE }}
		>
			<MessageCircle size={20} strokeWidth={2.2} />
		</div>
		<div className="min-w-0 flex-1">
			<div className="flex items-center justify-between gap-3">
				<p className="rubik-500 text-sm font-semibold text-gray-900">New message</p>
				<span className="shrink-0 rounded-full bg-[#F05252] px-2 py-0.5 text-[11px] font-semibold text-white">
					Patient
				</span>
			</div>
			<p className="mt-1 truncate text-sm text-gray-900">{patientName}</p>
			{preview ? (
				<p className="mt-0.5 truncate text-xs text-gray-500">{preview}</p>
			) : null}
			<p className="mt-2 text-xs font-medium" style={{ color: PATIENT_CHAT_BRAND_BLUE }}>
				Open chat
			</p>
		</div>
	</div>
);
const MESSAGE_GROUP_WINDOW_MS = 60 * 1000;
const UNREAD_SUMMARY_CACHE_MS = 5000;
const UNREAD_SUMMARY_STALE_GUARD_MS = 10000;
let currentUnreadSummary = { totalUnreadCount: 0, countsByPatientId: {} };
let unreadSummaryRequest = null;
let unreadSummaryFetchedAt = 0;
let unreadSummaryStaleGuardUntil = 0;

const toTitleCase = (value = '') => value
	.toString()
	.trim()
	.toLowerCase()
	.replace(/\s+/g, ' ')
	.replace(/\b[a-z]/g, (letter) => letter.toUpperCase());

const getPatientName = (patient) => {
	const lastFirstName = `${patient?.last_name || ''} ${patient?.first_name || ''}`.trim();
	const fallbackName = patient?.name || '';
	return toTitleCase(lastFirstName || fallbackName || 'Patient');
};

const normalizePatient = (patient = {}) => ({
	id: patient.id,
	first_name: patient.first_name,
	last_name: patient.last_name,
	name: patient.name || getPatientName(patient),
	phone: patient.phone,
	phn: patient.phn,
	route: patient.route,
});

const uniqueMessages = (messages) => {
	const seen = new Set();
	return messages.filter((message) => {
		const key = message?.id || `${message?.sender_type}-${message?.created_at}-${message?.message}`;
		if (seen.has(key)) {
			return false;
		}
		seen.add(key);
		return true;
	});
};

const getMessageTime = (message) => {
	if (!message?.created_at) return '';
	const date = new Date(message.created_at);
	if (Number.isNaN(date.getTime())) return '';

	return date.toLocaleTimeString([], {
		hour: 'numeric',
		minute: '2-digit',
	});
};

const getMessageTimestamp = (message) => {
	if (!message?.created_at) return 0;
	const timestamp = new Date(message.created_at).getTime();
	return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getMessageDateKey = (message) => {
	if (!message?.created_at) return '';
	const date = new Date(message.created_at);
	if (Number.isNaN(date.getTime())) return '';

	return [
		date.getFullYear(),
		String(date.getMonth() + 1).padStart(2, '0'),
		String(date.getDate()).padStart(2, '0'),
	].join('-');
};

const getMessageDateLabel = (message) => {
	if (!message?.created_at) return '';
	const date = new Date(message.created_at);
	if (Number.isNaN(date.getTime())) return '';

	const monthNames = [
		'January', 'February', 'March', 'April', 'May', 'June',
		'July', 'August', 'September', 'October', 'November', 'December',
	];

	return `${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

const groupMessages = (messages) => messages.reduce((groups, message) => {
	const lastGroup = groups[groups.length - 1];
	const sameSender = lastGroup?.senderType === message.sender_type;
	const previousMessage = lastGroup?.messages?.[lastGroup.messages.length - 1];
	const withinWindow = previousMessage
		? Math.abs(getMessageTimestamp(message) - getMessageTimestamp(previousMessage)) <= MESSAGE_GROUP_WINDOW_MS
		: false;

	if (sameSender && withinWindow) {
		lastGroup.messages.push(message);
		lastGroup.time = getMessageTime(message) || lastGroup.time;
		return groups;
	}

	groups.push({
		id: message.id || `${message.sender_type}-${message.created_at}-${groups.length}`,
		dateKey: getMessageDateKey(message),
		dateLabel: getMessageDateLabel(message),
		senderType: message.sender_type,
		time: getMessageTime(message),
		messages: [message],
	});

	return groups;
}, []);

const buildTimelineItems = (groups) => {
	let previousDateKey = '';
	return groups.flatMap((group) => {
		const items = [];
		if (group.dateKey && group.dateKey !== previousDateKey) {
			items.push({
				type: 'date',
				id: `date-${group.dateKey}`,
				label: group.dateLabel,
			});
			previousDateKey = group.dateKey;
		}
		items.push({
			type: 'messages',
			...group,
		});
		return items;
	});
};

const normalizeUnreadSummary = (payload = {}) => {
	const source = payload?.data || payload;
	if (source?.countsByPatientId) {
		const countsByPatientId = {};
		Object.entries(source.countsByPatientId).forEach(([patientId, count]) => {
			const numericPatientId = Number(patientId);
			const numericCount = Number(count) || 0;
			if (numericPatientId && numericCount > 0) {
				countsByPatientId[numericPatientId] = numericCount;
			}
		});
		return {
			totalUnreadCount: Number.isFinite(Number(source.totalUnreadCount))
				? Number(source.totalUnreadCount)
				: Object.values(countsByPatientId).reduce((sum, count) => sum + count, 0),
			countsByPatientId,
		};
	}

	const countsByPatientId = {};
	(source?.patients || []).forEach((patient) => {
		const patientId = Number(patient.patient_id || patient.id);
		const count = Number(patient.unread_count || 0);
		if (patientId && count > 0) {
			countsByPatientId[patientId] = count;
		}
	});

	const total = Number.isFinite(Number(source?.total_unread_count))
		? Number(source.total_unread_count)
		: Object.values(countsByPatientId).reduce((sum, count) => sum + count, 0);

	return { totalUnreadCount: total, countsByPatientId };
};

const emitUnreadChanged = (summary, options = {}) => {
	const normalized = normalizeUnreadSummary(summary);
	if (options.guardStaleResponses) {
		unreadSummaryStaleGuardUntil = Date.now() + UNREAD_SUMMARY_STALE_GUARD_MS;
	} else if (normalized.totalUnreadCount < currentUnreadSummary.totalUnreadCount) {
		unreadSummaryStaleGuardUntil = 0;
	}
	currentUnreadSummary = normalized;
	window.dispatchEvent(new CustomEvent(UNREAD_CHANGED_EVENT, { detail: normalized }));
	try {
		window.localStorage.setItem(UNREAD_STORAGE_KEY, JSON.stringify({
			at: Date.now(),
			summary: normalized,
		}));
	} catch (error) {
		// Ignore private-mode/storage failures; same-tab events still work.
	}
	return normalized;
};

const applyUnreadDelta = (current, patientId, delta, totalOverride = null) => {
	const id = Number(patientId);
	if (!id) return current;
	const countsByPatientId = { ...current.countsByPatientId };
	const nextPatientCount = Math.max(0, Number(countsByPatientId[id] || 0) + delta);
	if (nextPatientCount > 0) {
		countsByPatientId[id] = nextPatientCount;
	} else {
		delete countsByPatientId[id];
	}

	const totalUnreadCount = totalOverride === null
		? Object.values(countsByPatientId).reduce((sum, count) => sum + count, 0)
		: Math.max(0, Number(totalOverride) || 0);

	return { totalUnreadCount, countsByPatientId };
};

export const formatUnreadBadge = (count) => {
	const numeric = Number(count) || 0;
	if (numeric <= 0) return '';
	return numeric > 9 ? '9+' : String(numeric);
};

export const usePatientChatUnreadCounts = () => {
	const [summary, setSummary] = useState(currentUnreadSummary);

	const refreshUnreadCounts = useCallback(async () => {
		const now = Date.now();
		if (unreadSummaryFetchedAt && now - unreadSummaryFetchedAt < UNREAD_SUMMARY_CACHE_MS) {
			setSummary(currentUnreadSummary);
			return currentUnreadSummary;
		}

		try {
			if (!unreadSummaryRequest) {
				unreadSummaryRequest = Axios.get(pharmacy_patient_chats_unread_summary)
					.finally(() => {
						unreadSummaryRequest = null;
					});
			}

			const response = await unreadSummaryRequest;
			const nextSummary = normalizeUnreadSummary(response?.data?.data || response?.data);
			if (
				Date.now() < unreadSummaryStaleGuardUntil
				&& nextSummary.totalUnreadCount < currentUnreadSummary.totalUnreadCount
			) {
				setSummary(currentUnreadSummary);
				return currentUnreadSummary;
			}
			unreadSummaryFetchedAt = Date.now();
			setSummary(nextSummary);
			emitUnreadChanged(nextSummary);
			return nextSummary;
		} catch (error) {
			// Keep the last known count; unread badges should never break the header.
			setSummary(currentUnreadSummary);
			return currentUnreadSummary;
		}
	}, []);

	useEffect(() => {
		refreshUnreadCounts();
	}, [refreshUnreadCounts]);

	useEffect(() => {
		const onUnreadChanged = (event) => {
			const nextSummary = normalizeUnreadSummary(event.detail);
			currentUnreadSummary = nextSummary;
			setSummary(nextSummary);
		};
		const onStorage = (event) => {
			if (event.key !== UNREAD_STORAGE_KEY || !event.newValue) return;
			try {
				const parsed = JSON.parse(event.newValue);
				const nextSummary = normalizeUnreadSummary(parsed.summary);
				currentUnreadSummary = nextSummary;
				setSummary(nextSummary);
			} catch (error) {
				// Ignore malformed external storage events.
			}
		};

		window.addEventListener(UNREAD_CHANGED_EVENT, onUnreadChanged);
		window.addEventListener('storage', onStorage);
		return () => {
			window.removeEventListener(UNREAD_CHANGED_EVENT, onUnreadChanged);
			window.removeEventListener('storage', onStorage);
		};
	}, []);

	return { ...summary, refreshUnreadCounts };
};

const showPatientMessageNotification = ({ patient, message, onOpenPatientChat }) => {
	if (!message || message.sender_type !== 'patient') return;

	const patientName = getPatientName(patient || { name: message.sender_name });
	const preview = String(message.message || '').trim();
	const toastId = `${NOTIFICATION_TOAST_ID_PREFIX}-${message.id || `${message.patient_id}-${message.created_at}`}`;

	toast(
		<PatientChatMessageNotificationContent patientName={patientName} preview={preview} />,
		{
			toastId,
			position: 'top-right',
			autoClose: 6000,
			closeOnClick: true,
			hideProgressBar: true,
			icon: false,
			className: 'patient-chat-message-toast !w-[330px] !max-w-[330px]',
			bodyClassName: '!m-0 !p-0',
			style: PATIENT_CHAT_NOTIFICATION_TOAST_STYLE,
			onClick: () => onOpenPatientChat(patient || { id: message.patient_id }),
		}
	);

	if (window.Notification?.permission === 'granted') {
		new window.Notification('New patient message', {
			body: `${patientName}: ${preview}`,
		});
	}
};

export const PatientChatNotificationBridge = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const handleOpenPatientChat = useCallback((patient) => {
		openPatientChat(patient, navigate);
	}, [navigate]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const previewToken = params.get('preview_patient_chat_notification');
		if (!previewToken || location.pathname !== pharmacy_app_patient_chat) return undefined;

		const patientId = Number(params.get('patient_id'));
		if (!patientId) return undefined;

		let cancelled = false;
		const loadPreviewNotification = async () => {
			try {
				const response = await Axios.get(pharmacy_patient_chats(patientId));
				if (cancelled) return;

				const patient = normalizePatient(response?.data?.data?.patient || { id: patientId });
				const messages = response?.data?.data?.messages || [];
				const lastPatientMessage = [...messages].reverse().find((entry) => entry?.sender_type === 'patient');

				showPatientMessageNotification({
					patient,
					message: lastPatientMessage || {
						sender_type: 'patient',
						patient_id: patientId,
						message: 'hi',
						id: `preview-${previewToken}`,
					},
					onOpenPatientChat: handleOpenPatientChat,
				});
			} catch {
				if (cancelled) return;
				showPatientMessageNotification({
					patient: { id: patientId },
					message: {
						sender_type: 'patient',
						patient_id: patientId,
						message: 'hi',
						id: `preview-${previewToken}`,
					},
					onOpenPatientChat: handleOpenPatientChat,
				});
			}
		};

		loadPreviewNotification();
		return () => {
			cancelled = true;
		};
	}, [location.pathname, location.search, handleOpenPatientChat]);

	useEffect(() => {
		const chatSubscription = subscribeRealtimeEvent(CHAT_EVENT, ({ data }) => {
			const message = data?.message;
			if (message?.sender_type !== 'patient' || !message?.patient_id) return;

			emitUnreadChanged(
				applyUnreadDelta(currentUnreadSummary, message.patient_id, 1),
				{ guardStaleResponses: true },
			);
			showPatientMessageNotification({
				patient: data?.patient,
				message,
				onOpenPatientChat: handleOpenPatientChat,
			});
		});

		const readSubscription = subscribeRealtimeEvent(READ_EVENT, ({ data }) => {
			if (!data?.patient_id) return;
			const patientId = Number(data.patient_id);
			emitUnreadChanged(applyUnreadDelta(
				currentUnreadSummary,
				patientId,
				-Number(currentUnreadSummary.countsByPatientId?.[patientId] || 0),
				data.total_unread_count,
			));
		});

		return () => {
			chatSubscription?.close?.();
			readSubscription?.close?.();
		};
	}, [handleOpenPatientChat]);

	return null;
};

const PatientChatLauncher = () => {
	const location = useLocation();
	const [open, setOpen] = useState(false);
	const [selectedPatient, setSelectedPatient] = useState(null);
	const [patientSearch, setPatientSearch] = useState('');
	const [patients, setPatients] = useState([]);
	const [patientsLoading, setPatientsLoading] = useState(false);
	const [messages, setMessages] = useState([]);
	const [messagesLoading, setMessagesLoading] = useState(false);
	const [draft, setDraft] = useState('');
	const [sending, setSending] = useState(false);
	const [error, setError] = useState('');
	const messagesEndRef = useRef(null);
	const messagesScrollRef = useRef(null);
	const messagesRequestRef = useRef(0);
	const patientListRef = useRef(null);
	const { countsByPatientId } = usePatientChatUnreadCounts();

	const selectedPatientName = useMemo(() => getPatientName(selectedPatient), [selectedPatient]);
	const groupedMessages = useMemo(() => groupMessages(messages), [messages]);
	const timelineItems = useMemo(() => buildTimelineItems(groupedMessages), [groupedMessages]);
	const selectedPatientIndex = useMemo(() => {
		if (!selectedPatient?.id) return -1;
		return patients.findIndex((patient) => Number(patient.id) === Number(selectedPatient.id));
	}, [patients, selectedPatient?.id]);

	const loadPatients = useCallback(async (search = '') => {
		const normalizedSearch = search.trim();
		setPatientsLoading(true);
		setError('');
		try {
			const response = await Axios.get(pharmacy_patient_chat_patients, {
				params: {
					limit: 20,
					search: normalizedSearch || undefined,
					conversations_only: normalizedSearch ? undefined : 1,
				},
			});
			setPatients(response?.data?.data?.patients || []);
		} catch (requestError) {
			setPatients([]);
			setError(requestError?.response?.data?.message || 'Unable to load patients.');
		} finally {
			setPatientsLoading(false);
		}
	}, []);

	const loadMessages = useCallback(async (patient) => {
		if (!patient?.id) {
			return;
		}

		const requestId = messagesRequestRef.current + 1;
		messagesRequestRef.current = requestId;
		setMessagesLoading(true);
		setError('');
		try {
			const response = await Axios.get(pharmacy_patient_chats(patient.id));
			if (messagesRequestRef.current !== requestId) return;
			setMessages(response?.data?.data?.messages || []);
			setSelectedPatient((current) => ({
				...(current || {}),
				...normalizePatient(response?.data?.data?.patient || patient),
			}));
		} catch (requestError) {
			if (messagesRequestRef.current !== requestId) return;
			setError(requestError?.response?.data?.message || 'Unable to load chat.');
		} finally {
			if (messagesRequestRef.current === requestId) {
				setMessagesLoading(false);
			}
		}
	}, []);

	const markPatientRead = useCallback(async (patientId) => {
		if (!patientId) return;
		try {
			const response = await Axios.post(pharmacy_patient_chats_read(patientId));
			const data = response?.data?.data || {};
			const id = Number(patientId);
			emitUnreadChanged(applyUnreadDelta(
				currentUnreadSummary,
				id,
				-Number(currentUnreadSummary.countsByPatientId?.[id] || 0),
				data.total_unread_count,
			));
		} catch (requestError) {
			// Reading chat should not block the conversation UI.
		}
	}, []);

	const selectPatient = useCallback((patient) => {
		const normalized = normalizePatient(patient);
		setSelectedPatient(normalized);
		setOpen(true);
		setMessages([]);
		setDraft('');
		setError('');
		loadMessages(normalized);
		markPatientRead(normalized.id);
	}, [loadMessages, markPatientRead]);

	useEffect(() => {
		const handler = (event) => {
			setOpen(true);
			setError('');

			if (event?.detail?.patient?.id) {
				selectPatient(event.detail.patient);
				return;
			}

			if (!selectedPatient) {
				loadPatients(patientSearch);
			}
		};

		window.addEventListener(OPEN_EVENT, handler);
		return () => window.removeEventListener(OPEN_EVENT, handler);
	}, [loadPatients, patientSearch, selectPatient, selectedPatient]);

	useEffect(() => {
		if (!open) {
			return;
		}

		const timer = window.setTimeout(() => loadPatients(patientSearch), 250);
		return () => window.clearTimeout(timer);
	}, [loadPatients, open, patientSearch]);

	useEffect(() => {
		const subscription = subscribeRealtimeEvent(CHAT_EVENT, ({ data }) => {
			const nextMessage = data?.message;
			if (!nextMessage?.patient_id) {
				return;
			}

			if (!patientSearch.trim()) {
				loadPatients('');
			}

			if (open && selectedPatient?.id && Number(nextMessage.patient_id) === Number(selectedPatient.id)) {
				setMessages((current) => uniqueMessages([...current, nextMessage]));
				if (nextMessage.sender_type === 'patient') {
					markPatientRead(nextMessage.patient_id);
				}
			}
		});

		return () => subscription?.close?.();
	}, [loadPatients, markPatientRead, open, patientSearch, selectedPatient?.id]);

	useEffect(() => {
		const messagesContainer = messagesScrollRef.current;
		if (!messagesContainer) return;

		window.requestAnimationFrame(() => {
			messagesContainer.scrollTop = messagesContainer.scrollHeight;
		});
	}, [messages.length, open, selectedPatient?.id]);

	useEffect(() => {
		if (selectedPatientIndex < 0) return;
		const selectedButton = patientListRef.current?.querySelector(`[data-patient-index="${selectedPatientIndex}"]`);
		selectedButton?.scrollIntoView?.({ block: 'nearest' });
	}, [selectedPatientIndex]);

	const selectPatientByOffset = useCallback((offset) => {
		if (patients.length === 0) return;
		const currentIndex = selectedPatientIndex >= 0 ? selectedPatientIndex : (offset > 0 ? -1 : 0);
		const nextIndex = Math.min(Math.max(currentIndex + offset, 0), patients.length - 1);
		const nextPatient = patients[nextIndex];
		if (nextPatient) {
			selectPatient(nextPatient);
		}
	}, [patients, selectPatient, selectedPatientIndex]);

	const handlePatientNavigationKeyDown = useCallback((event) => {
		if (event.key === 'ArrowDown') {
			event.preventDefault();
			selectPatientByOffset(1);
			return;
		}
		if (event.key === 'ArrowUp') {
			event.preventDefault();
			selectPatientByOffset(-1);
			return;
		}
		if (event.key === 'Enter' && selectedPatientIndex >= 0 && patients[selectedPatientIndex]) {
			event.preventDefault();
			selectPatient(patients[selectedPatientIndex]);
		}
	}, [patients, selectPatient, selectPatientByOffset, selectedPatientIndex]);

	const closeLauncher = () => {
		setOpen(false);
		setError('');
	};

	const sendMessage = async (event) => {
		event.preventDefault();
		const messageText = draft.trim();
		if (!messageText || !selectedPatient?.id || sending) {
			return;
		}

		setSending(true);
		setError('');
		try {
			const response = await Axios.post(pharmacy_patient_chats(selectedPatient.id), {
				message: messageText,
			});
			const savedMessage = response?.data?.data?.message;
			if (savedMessage) {
				setMessages((current) => uniqueMessages([...current, savedMessage]));
			}
			if (!patientSearch.trim()) {
				setPatients((currentPatients) => {
					const normalized = normalizePatient(selectedPatient);
					return [
						normalized,
						...currentPatients.filter((patient) => Number(patient.id) !== Number(normalized.id)),
					];
				});
			}
			setDraft('');
		} catch (requestError) {
			setError(requestError?.response?.data?.message || 'Unable to send message.');
		} finally {
			setSending(false);
		}
	};

	const isPatientChatPage = location.pathname === pharmacy_app_patient_chat;

	if (isPatientChatPage || !open) {
		return null;
	}

	return (
		<div className="fixed inset-0 pointer-events-none" style={{ zIndex: 9999 }}>
			<div className="absolute right-5 top-24 bottom-24 w-[900px] max-w-[calc(100vw-32px)] min-h-[520px] bg-white border border-gray-200 rounded-lg shadow-2xl pointer-events-auto flex overflow-hidden">
				<aside className="w-[320px] min-w-[280px] border-r border-gray-200 bg-white flex flex-col">
					<div className="bg-[#1A56DB] text-white px-4 py-4">
						<p className="font-semibold text-base leading-tight">Patient Chat</p>
						<p className="text-xs opacity-90 mt-1">Conversations and patient search</p>
					</div>
					<div className="p-3 border-b">
						<div className="relative">
							<Search size={16} className="absolute left-3 top-3 text-gray-400" />
							<input
								value={patientSearch}
								onChange={(event) => setPatientSearch(event.target.value)}
								onKeyDown={handlePatientNavigationKeyDown}
								className="w-full border rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
								placeholder="Search patient"
							/>
						</div>
					</div>
					<div ref={patientListRef} className="flex-1 overflow-auto" onKeyDown={handlePatientNavigationKeyDown}>
						{patientsLoading ? (
							<PatientListSkeleton />
						) : patients.length === 0 ? (
							<p className="p-4 text-sm text-gray-500">
								{patientSearch.trim() ? 'No patients found.' : 'No conversations yet.'}
							</p>
						) : patients.map((patient, index) => (
							<PatientListButton
								key={patient.id}
								patient={patient}
								index={index}
								unreadCount={countsByPatientId?.[Number(patient.id)] || 0}
								selected={Number(selectedPatient?.id) === Number(patient.id)}
								onSelect={selectPatient}
							/>
						))}
					</div>
					{error && !selectedPatient && <p className="px-4 py-2 text-xs text-red-600 border-t">{error}</p>}
				</aside>

				<section className="min-w-0 flex-1 bg-gray-50 flex flex-col">
					<div className="bg-[#1A56DB] text-white px-5 py-4 flex items-center justify-between">
						<div className="min-w-0">
							<p className="font-semibold text-base leading-tight truncate">
								{selectedPatient ? selectedPatientName : 'Select a patient'}
							</p>
							<p className="text-xs opacity-90 mt-1 truncate">
								{selectedPatient ? (selectedPatient.phone || selectedPatient.phn || 'Active patient') : 'Conversation will appear here'}
							</p>
						</div>
						<button
							type="button"
							onClick={closeLauncher}
							className="ml-4 w-9 h-9 rounded-md flex items-center justify-center text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/40"
							aria-label="Close patient chat"
							title="Close Patient Chat"
						>
							<X size={24} />
						</button>
					</div>

					<div ref={messagesScrollRef} className="flex-1 overflow-auto bg-gray-50 p-5">
						{!selectedPatient ? (
							<div className="h-full flex items-center justify-center text-center text-sm text-gray-500">
								<div>
									<MessageCircle size={34} className="mx-auto mb-3 text-gray-400" />
									<p>Select a patient to start chatting.</p>
								</div>
							</div>
						) : messagesLoading ? (
							<ChatMessageSkeleton />
						) : messages.length === 0 ? (
							<div className="h-full flex items-center justify-center text-center text-sm text-gray-500">
								<div>
									<MessageCircle size={30} className="mx-auto mb-3 text-gray-400" />
									<p>No messages yet.</p>
								</div>
							</div>
						) : timelineItems.map((item) => {
							if (item.type === 'date') {
							return (
								<div key={item.id} className="my-5 flex justify-center">
									<span className="inline-flex h-9 items-center rounded-md bg-blue-100 px-4 text-sm font-semibold text-blue-800 shadow-sm ring-1 ring-blue-200">
										{item.label}
									</span>
								</div>
								);
							}

							const group = item;
							const fromPharmacy = group.senderType === 'pharmacy';
							return (
								<div key={group.id} className={`mb-4 flex ${fromPharmacy ? 'justify-end' : 'justify-start'}`}>
									<div className={`max-w-[70%] flex flex-col gap-1.5 ${fromPharmacy ? 'items-end' : 'items-start'}`}>
										{group.messages.map((message, index) => (
											<div
												key={message.id || `${message.created_at}-${message.message}-${index}`}
												className={`rounded-2xl px-4 py-3 text-sm shadow-sm ${fromPharmacy ? 'bg-[#2563eb] text-white rounded-br-md' : 'bg-white text-gray-900 border rounded-bl-md'}`}
											>
												<p className="whitespace-pre-wrap break-words text-base">{message.message}</p>
											</div>
										))}
										{group.time && (
											<p className={`text-xs px-1 ${fromPharmacy ? 'text-gray-500 text-right' : 'text-gray-500'}`}>
												{group.time}{fromPharmacy ? ' • You' : ''}
											</p>
										)}
									</div>
								</div>
							);
						})}
						<div ref={messagesEndRef} />
					</div>

					<form onSubmit={sendMessage} className="border-t p-4 bg-white">
						{error && selectedPatient && <p className="text-xs text-red-600 mb-2">{error}</p>}
						<div className="flex gap-3">
							<input
								value={draft}
								onChange={(event) => setDraft(event.target.value)}
								disabled={!selectedPatient}
								className="flex-1 border rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-gray-100"
								placeholder={selectedPatient ? 'Type a message' : 'Select a patient to message'}
							/>
							<button
								type="submit"
								disabled={!selectedPatient || sending || !draft.trim()}
								className="bg-[#2563eb] disabled:bg-gray-300 text-white rounded-md px-4 py-3 flex items-center justify-center"
								aria-label="Send patient message"
							>
								<Send size={20} />
							</button>
						</div>
					</form>
				</section>
			</div>
		</div>
	);
};

const PatientListButton = ({ patient, index, unreadCount, selected, onSelect }) => {
	const badge = formatUnreadBadge(unreadCount);

	return (
		<button
			type="button"
			data-patient-index={index}
			onClick={() => onSelect(patient)}
			className={`w-full text-left px-4 py-3 border-b hover:bg-blue-50 flex items-center gap-3 ${selected ? 'bg-blue-50' : 'bg-white'}`}
		>
			<div className="min-w-0 flex-1">
				<p className="text-sm font-medium text-gray-900 truncate">{getPatientName(patient)}</p>
				<p className="text-xs text-gray-500 truncate">{patient.phone || patient.phn || patient.route || 'Patient'}</p>
			</div>
			{badge && (
				<span className="inline-flex min-w-[20px] h-5 px-1.5 items-center justify-center rounded-full bg-red-600 text-white text-[11px] font-bold">
					{badge}
				</span>
			)}
		</button>
	);
};

const PatientListSkeleton = () => (
	<div className="animate-pulse">
		{Array.from({ length: 8 }).map((_, index) => (
			<div key={index} className="px-4 py-4 border-b bg-white">
				<div className="h-4 w-40 rounded bg-gray-200" />
				<div className="mt-3 h-3 w-24 rounded bg-gray-200" />
			</div>
		))}
	</div>
);

const ChatMessageSkeleton = () => (
	<div className="space-y-5 animate-pulse">
		<div className="flex justify-start">
			<div className="w-[190px] rounded-lg border bg-white px-4 py-4 shadow-sm">
				<div className="h-4 w-12 rounded bg-gray-200" />
				<div className="mt-5 h-3 w-32 rounded bg-gray-200" />
			</div>
		</div>
		<div className="flex justify-end">
			<div className="w-[135px] rounded-lg bg-blue-200 px-4 py-4 shadow-sm">
				<div className="h-4 w-10 rounded bg-blue-100" />
				<div className="mt-5 h-3 w-20 rounded bg-blue-100" />
			</div>
		</div>
		<div className="flex justify-start">
			<div className="w-[205px] rounded-lg border bg-white px-4 py-4 shadow-sm">
				<div className="h-4 w-16 rounded bg-gray-200" />
				<div className="mt-5 h-3 w-36 rounded bg-gray-200" />
			</div>
		</div>
		<div className="flex justify-start">
			<div className="w-[170px] rounded-lg border bg-white px-4 py-4 shadow-sm">
				<div className="h-4 w-14 rounded bg-gray-200" />
				<div className="mt-5 h-3 w-28 rounded bg-gray-200" />
			</div>
		</div>
	</div>
);

export const openPatientChat = (patient = null, navigate = null) => {
	const patientId = Number(patient?.id);
	const targetUrl = patientId
		? `${pharmacy_app_patient_chat}?patient_id=${patientId}`
		: pharmacy_app_patient_chat;

	if (typeof navigate === 'function') {
		navigate(targetUrl);
		return;
	}

	window.history.pushState({}, '', targetUrl);
	window.dispatchEvent(new PopStateEvent('popstate'));
};

export default PatientChatLauncher;
